import { Label, Stack } from "@fluentui/react";
import React, { useState } from "react";
import { Control, Controller, DeepMap, FieldError } from "react-hook-form";

import { FormValidationRules } from "../../utils/types/FormValidationRules";
import { getValidate } from "../../utils/validation/getValidate";
import { FailureSeverity, ValidationFailure } from "../../utils/validation/ValidationResult";
import { AutocompleteDropdown } from "../inputs/dropdown/AutocompleteDropdown";
import { DropdownOption } from "../inputs/dropdown/DropdownOption";
import { FormErrorMessage } from "./FormErrorMessage";
import { FormFailureMessages } from "./FormFailureMessages";
import { getFormLabelText } from "./getFormLabelText";
import { useFormWatch } from "./useFormWatch";

export interface FormAutocompleteProps {
    placeholder?: string;
    initOptions: DropdownOption[];
    updateInitialOptionsUponTheirChange?: boolean;
    control: Control;
    name: string;
    errors: DeepMap<Record<string, unknown>, FieldError>;
    label: string;
    rules?: FormAutocompleteRules;
    testId?: string;
    disabled?: boolean;
    minSearchLength?: number;
    onChange?(value: string): void;
    onCustomSearch(query: string): Promise<DropdownOption[]>;
    renderErrorMsg?(): JSX.Element;
    dependsOn?: string[];
}

export const FormAutocomplete = (props: FormAutocompleteProps): JSX.Element => {
    const [warnings, setWarnings] = useState<ValidationFailure[]>([]);

    const clearWarnings = () => {
        setWarnings([]);
    };

    const handleOnChange = () => {
        if (props.onChange) {
            props.onChange("");
            clearWarnings();
        }
    };

    if (props.dependsOn) {
        useFormWatch({
            name: props.name,
            control: props.control,
            dependsOn: props.dependsOn,
            onClear: handleOnChange
        });
    }

    const handleCustomSearch = async (query?: string): Promise<DropdownOption[]> => {
        if (!query) return [];

        if (props.onCustomSearch) return props.onCustomSearch(query);

        return [];
    };

    const render = (renderProp: { value: string | undefined; onChange(value?: string): void }) => {
        const handleItemSelected = (newValue: string | undefined) => {
            clearWarnings();
            renderProp.onChange(newValue ?? "");

            if (props.onChange) {
                props.onChange(newValue ?? "");
            }
        };

        return (
            <Stack>
                <Label>{getFormLabelText(props.label, !!props.rules?.required)}</Label>
                <Stack>
                    <AutocompleteDropdown
                        placeholder={props.placeholder ?? ""}
                        selectedKey={renderProp.value}
                        minSearchLength={props.minSearchLength}
                        onSearch={handleCustomSearch}
                        onChange={handleItemSelected}
                        disabled={props.disabled}
                        initialOptions={props.initOptions}
                        updateInitialOptionsUponTheirChange={props.updateInitialOptionsUponTheirChange}
                        testId={props.testId}
                        errors={props.errors}
                        name={props.name}
                    />
                </Stack>
            </Stack>
        );
    };

    return (
        <Stack>
            <Controller
                name={props.name}
                control={props.control}
                rules={{
                    ...props.rules,
                    validate: getValidate<string, FormAutocompleteRules>(
                        props.rules,
                        props.control.formStateRef.current.submitCount,
                        setWarnings
                    )
                }}
                render={render}
            />
            {props.renderErrorMsg ? (
                props.renderErrorMsg()
            ) : (
                <FormErrorMessage name={props.name} errors={props.errors} testId={`${props.name}-errors`} />
            )}
            <FormFailureMessages failures={warnings} severity={FailureSeverity.Warning} />
        </Stack>
    );
};

export type FormAutocompleteRules = FormValidationRules<string>;
